import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from "@mui/material";

/**
 * What the articulation pass rewrote, shown before it is applied.
 *
 * The backend's LLM turns the reviewed clinical data into prose, and that
 * result used to be written straight over the doctor's own values. They then
 * signed a document whose sentences they had never seen as a change.
 *
 * Rejecting is a first-class outcome: the document is then built from the
 * doctor's own wording. Accept is the prominent action but never automatic.
 *
 * The count leads deliberately — the articulation usually rewrites most prose
 * fields, and a wall of undifferentiated diff invites blind acceptance.
 */
const ArticulationDiffDialog = ({ open, changes = [], onAccept, onReject, busy = false }) => (
  <Dialog open={open} onClose={busy ? undefined : onReject} fullWidth maxWidth="md">
    <DialogTitle>Review the generated wording</DialogTitle>
    <DialogContent dividers>
      {changes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          The generated summary matches what you entered — nothing was rewritten.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>
              {changes.length} field{changes.length === 1 ? " was" : "s were"} rewritten.
            </strong>{" "}
            Accept to build the document from the generated wording, or keep your own.
          </Typography>
          <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
            {changes.map((c, i) => (
              <Box key={c.path}>
                {i > 0 && <Divider sx={{ my: 1.5 }} />}
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {c.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "error.main",
                    textDecoration: "line-through",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {c.before || "(empty)"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, color: "success.dark", whiteSpace: "pre-wrap" }}
                >
                  {c.after || "(empty)"}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onReject} variant="outlined" color="secondary" disabled={busy}>
        Use my wording
      </Button>
      <Button onClick={onAccept} variant="contained" disabled={busy}>
        {busy ? "Building…" : "Accept generated wording"}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ArticulationDiffDialog;
